import { z } from 'zod';
/**
 * Marca de la entidad (GUIA-AGENTES.md §3.11): logo y color principal de las pantallas de todas las apps (ingreso,
 * portal, barra lateral, correos). El membrete de los documentos no va aquí: es de SGDEA.
 */
export const BRAND_THEME_CODES = ['DEFAULT', 'AZUL', 'CYAN', 'VERDE', 'ROJO', 'NARANJA', 'MORADO', 'GRIS', 'CUSTOM'];
/** Temas predefinidos (los de SGDEA). `DEFAULT` deja los colores del sistema de diseño. */
export const BRAND_THEMES = [
    { code: 'DEFAULT', label: 'Datalog (predeterminado)', primary: null },
    { code: 'AZUL', label: 'Azul institucional', primary: '#1d4ed8' },
    { code: 'CYAN', label: 'Cyan', primary: '#00ccff' },
    { code: 'VERDE', label: 'Verde', primary: '#15803d' },
    { code: 'ROJO', label: 'Rojo', primary: '#b91c1c' },
    { code: 'NARANJA', label: 'Naranja', primary: '#c2410c' },
    { code: 'MORADO', label: 'Morado', primary: '#7e22ce' },
    { code: 'GRIS', label: 'Gris pizarra', primary: '#334155' },
];
export const HEX_COLOR = /^#[0-9a-f]{6}$/i;
/** Código de tema de otra app (SGDEA guardaba `XILTRION` para el predeterminado). */
export function normalizeBrandTheme(code) {
    if (!code || code === 'XILTRION')
        return 'DEFAULT';
    return BRAND_THEME_CODES.includes(code) ? code : 'DEFAULT';
}
export const brandingSchema = z
    .object({
    theme: z.preprocess((v) => (typeof v === 'string' ? normalizeBrandTheme(v) : v), z.enum(BRAND_THEME_CODES)).default('DEFAULT'),
    customColor: z.string().regex(HEX_COLOR, 'Color en formato #RRGGBB').nullable().optional().transform((v) => (v ? v.toLowerCase() : null)),
})
    .refine((v) => v.theme !== 'CUSTOM' || !!v.customColor, { message: 'Elige el color personalizado', path: ['customColor'] });
/** Logo: PNG, JPEG o WebP. SVG no, porque se sirve público y un SVG puede llevar código. */
export const BRAND_LOGO_MIMES = ['image/png', 'image/jpeg', 'image/webp'];
export const BRAND_LOGO_ACCEPT = 'image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp';
export const BRAND_LOGO_MAX_BYTES = 512 * 1024;
export const brandLogoUploadSchema = z.object({
    fileName: z.string().trim().min(1).max(200),
    /** La imagen en base64, sin el prefijo `data:`. */
    base64: z.string().min(1).max(Math.ceil((BRAND_LOGO_MAX_BYTES * 4) / 3) + 8),
});
/** Reconoce la imagen por sus primeros bytes (no por el nombre). */
export function sniffLogoMime(bytes) {
    if (bytes.length > 8 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47)
        return 'image/png';
    if (bytes.length > 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff)
        return 'image/jpeg';
    if (bytes.length > 12 && String.fromCharCode(...bytes.slice(0, 4)) === 'RIFF' && String.fromCharCode(...bytes.slice(8, 12)) === 'WEBP')
        return 'image/webp';
    return null;
}
/** Color principal efectivo de un tema (`null` = el del sistema de diseño). */
export function brandPrimary(theme, customColor) {
    if (theme === 'CUSTOM')
        return customColor && HEX_COLOR.test(customColor) ? customColor.toLowerCase() : null;
    return BRAND_THEMES.find((t) => t.code === theme)?.primary ?? null;
}
/** Texto legible sobre un color: blanco o casi negro según su luminancia (WCAG). */
export function readableOn(hex) {
    const n = hex.replace('#', '');
    const [r, g, b] = [0, 2, 4].map((i) => {
        const c = parseInt(n.slice(i, i + 2), 16) / 255;
        return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    });
    const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    const DARK = 0.0030; // luminancia de #0a0a0a
    return 1.05 / (lum + 0.05) >= (lum + 0.05) / (DARK + 0.05) ? '#ffffff' : '#0a0a0a';
}
//# sourceMappingURL=marca.js.map